// Support.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Instagram, 
  Mail, 
  MessageCircle, 
  Clock, 
  CheckCircle,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Support = () => {
  const navigate = useNavigate();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedInsta, setCopiedInsta] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('support@thearenax.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleCopyInsta = () => {
    navigator.clipboard.writeText('the.arenax');
    setCopiedInsta(true);
    setTimeout(() => setCopiedInsta(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 md:px-8 py-3">
          <div className="flex items-center gap-4 max-w-7xl mx-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-purple-400" />
            </motion.button>
            
            <div>
              <h1 className="text-xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                Support Center
              </h1>
              <p className="text-gray-400 text-xs">We're here to help you 24/7</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        {/* Quick Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-white font-rajdhani font-bold text-lg">How can we help?</h2>
                <p className="text-gray-400 text-sm">
                  Choose your preferred way to connect with us. Our support team is available 
                  to assist you with any questions or issues.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Support Options */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Instagram Support */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group relative overflow-hidden bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-2xl p-6 border border-pink-500/30 hover:border-pink-500/50 transition-all duration-300"
          >
            {/* Background glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/0 to-purple-500/0 group-hover:from-pink-500/5 group-hover:to-purple-500/5 transition-all duration-500" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-pink-500/20">
                  <Instagram className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-rajdhani font-bold text-lg">Instagram DM</h3>
                  <p className="text-gray-400 text-sm">Quick response within 24 hours</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3 border border-white/5">
                  <span className="text-gray-300 text-sm font-rajdhani">@the.arenax</span>
                  <button
                    onClick={handleCopyInsta}
                    className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm"
                  >
                    {copiedInsta ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                <a
                  href="https://instagram.com/the.arenax"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-rajdhani font-bold py-3 rounded-xl hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat on Instagram
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="mt-4 flex items-center gap-2 text-green-400 text-xs">
                <CheckCircle className="w-4 h-4" />
                <span>Usually responds within 12-24 hours</span>
              </div>
            </div>
          </motion.div>

          {/* Email Support */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="group relative overflow-hidden bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-6 border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300"
          >
            {/* Background glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/5 group-hover:to-purple-500/5 transition-all duration-500" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-rajdhani font-bold text-lg">Email Support</h3>
                  <p className="text-gray-400 text-sm">Detailed support via email</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3 border border-white/5">
                  <span className="text-gray-300 text-sm font-rajdhani">support@thearenax.com</span>
                  <button
                    onClick={handleCopyEmail}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    {copiedEmail ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                <a
                  href="mailto:support@thearenax.com"
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-rajdhani font-bold py-3 rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
                >
                  <Mail className="w-4 h-4" />
                  Send Email
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="mt-4 flex items-center gap-2 text-blue-400 text-xs">
                <Clock className="w-4 h-4" />
                <span>Usually responds within 24-48 hours</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8"
        >
          <h2 className="text-white font-rajdhani font-bold text-xl mb-4 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                question: "How do I withdraw my winnings?",
                answer: "Go to Wallet section and click on Withdraw. Your winnings will be transferred within 24-48 hours."
              },
              {
                question: "How to join a tournament?",
                answer: "Navigate to Matches section, select your preferred game, and click on Join Tournament."
              },
              {
                question: "What games are available?",
                answer: "We currently support BGMI, Free Fire, and other popular battle royale games."
              },
              {
                question: "When my wining will add?",
                answer: "With in 24 hour after the match is completed."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                className="bg-white/5 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-all duration-300"
              >
                <h4 className="text-white font-rajdhani font-semibold text-sm mb-2">
                  {faq.question}
                </h4>
                <p className="text-gray-400 text-sm">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Response Time Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-3 bg-white/5 rounded-full px-6 py-3 border border-white/5">
            <Clock className="w-5 h-5 text-purple-400" />
            <span className="text-gray-300 text-sm">
              Average response time: <span className="text-purple-400 font-rajdhani font-bold">12-24 hours</span>
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Support;